import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAppointmentTypeMatchingClient, getProviderSelectionClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { CACHE_TIME_10_MINS } from '@/lib/query-client';
import { SIMULATE_BATCH_MAX_TASKS } from '@vagusx/clients';
import type {
  SimulateRequest,
  UpdateProviderSelectionSettingsRequest,
  BatchProviderRanking,
  ReferralSignals,
} from '@vagusx/clients';

/**
 * Mutation hook to simulate appointment type selection.
 * Returns the recommendation, confidence level, and full score breakdown.
 */
export function useSimulateAppointmentType() {
  return useMutation({
    mutationFn: (request: SimulateRequest) => getAppointmentTypeMatchingClient().simulate(request),
  });
}

/**
 * Preview the appointment type that would be selected for a referral TASK.
 * The server derives the referral signals from the task's linked document and
 * runs the matcher. Read-only — used by the Tasks Overview. `enabled` lets the
 * caller skip the call for tasks with no resolvable referral document.
 */
export function useSimulateAppointmentTypeForTask(taskId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.appointmentTypeMatching.forTask(taskId),
    queryFn: () => getAppointmentTypeMatchingClient().simulateForTask(taskId),
    enabled,
    staleTime: CACHE_TIME_10_MINS,
  });
}

/**
 * Ranked provider pool for a whole page of referral tasks.
 *
 * Returned as a Map so a row cell is a lookup rather than a scan. One query for
 * the page (not one per row) is the whole point: see
 * `simulateBatchRequestSchema` for what per-row would have cost.
 *
 * The endpoint caps a request at `SIMULATE_BATCH_MAX_TASKS`, and the table's
 * rows-per-page selector goes higher than that — so the request is SPLIT here
 * rather than assuming the page fits. Splitting inside `queryFn` keeps it a
 * single cache entry with a stable `data` reference, which `useQueries` would
 * not give us. The cap stays what it should be: a server-side bound, not a
 * contract the caller has to remember.
 */
export function useReferralProviderRanking(taskIds: string[]): {
  byTaskId: Map<string, BatchProviderRanking>;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.appointmentTypeMatching.providersForTasks(taskIds),
    queryFn: async () => {
      const client = getAppointmentTypeMatchingClient();
      const batches = splitIntoBatches(taskIds, SIMULATE_BATCH_MAX_TASKS);
      const pages = await Promise.all(batches.map((ids) => client.simulateProvidersForTasks(ids)));
      return pages.flatMap((p) => p.results);
    },
    enabled: taskIds.length > 0,
    staleTime: CACHE_TIME_10_MINS,
  });

  return {
    byTaskId: useMemo(() => new Map((data ?? []).map((r) => [r.taskId, r])), [data]),
    // `enabled: false` leaves a query pending forever; an empty page is not
    // loading, it's empty.
    isLoading: taskIds.length > 0 && isLoading,
  };
}

/**
 * Split a page of ids into request-sized batches, preserving order.
 *
 * Exported for test: the server rejects anything over `SIMULATE_BATCH_MAX_TASKS`
 * with a 422, and a 422 here is invisible — the query errors, the lookup map is
 * empty, and every cell renders a dash as though no provider matched.
 */
export function splitIntoBatches<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Re-match a referral against ONE (provider, location) pair.
 *
 * `appointment_type_eligibility` is a (provider, location, type) triple, so
 * choosing a provider is itself a disambiguator: a type the org as a whole
 * cannot decide between may be the only one THIS clinician is enabled for.
 * Confirmed against production — an org-wide dead tie between two consult types
 * resolves to a single winner once scoped to a provider whose row for the other
 * one is disabled.
 *
 * Scoped to a pair only; skipped until both are known, since half a pair cannot
 * resolve the opt-in decision (ADR-099).
 */
export function useScopedTypeRecommendation(
  signals: ReferralSignals | undefined,
  providerId: string,
  locationId: string
) {
  const signalsKey = signals ? JSON.stringify(signals) : '';
  return useQuery({
    queryKey: queryKeys.appointmentTypeMatching.scopedToProvider(
      providerId,
      locationId,
      signalsKey
    ),
    queryFn: () =>
      getAppointmentTypeMatchingClient().simulate({ signals: signals!, providerId, locationId }),
    enabled: !!signals && !!providerId && !!locationId,
    staleTime: CACHE_TIME_10_MINS,
  });
}

/**
 * Hook to list all saved tie-break preferences for the organization.
 */
export function useAppointmentTypePreferences() {
  return useQuery({
    queryKey: queryKeys.appointmentTypeMatching.preferences(),
    queryFn: () => getAppointmentTypeMatchingClient().listPreferences(),
  });
}

/**
 * Mutation to delete a preference.
 */
export function useDeletePreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getAppointmentTypeMatchingClient().deletePreference(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointmentTypeMatching.all,
      });
    },
  });
}

// =============================================================================
// Provider selection settings
//
// The weights the ranking algorithm runs on. The simulator sends per-run
// overrides on `simulate` for free tuning; these two hooks are only for reading
// and explicitly SAVING the org default.
// =============================================================================

/** The org's saved ranking policy. Seeded server-side, so this never 404s. */
export function useProviderSelectionSettings() {
  return useQuery({
    queryKey: queryKeys.providerSelection.settings(),
    queryFn: () => getProviderSelectionClient().getSettings(),
    staleTime: CACHE_TIME_10_MINS,
  });
}

/** Persist the ranking policy — the "Save as org default" action. */
export function useUpdateProviderSelectionSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateProviderSelectionSettingsRequest) =>
      getProviderSelectionClient().updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.providerSelection.all });
    },
  });
}
